/**
 * Calendar days in Asia/Baghdad, the day every report is counted in.
 *
 * Baghdad is a FIXED UTC+03:00 — Iraq dropped daylight saving in 2008 — so a
 * day here is plain arithmetic on an offset, not a job for `Intl` time-zone
 * data. That matters more than it sounds: the browser's own zone is whatever
 * the operator's laptop says (often UTC on a jump host, or Europe for a
 * travelling admin), and "today" computed in it would disagree with the
 * server's "today" for three hours every night. The backend
 * (`controller/services/report_time.py`) uses the same fixed offset, so a day
 * chosen here is the same 24 hours the server counts.
 *
 * Days travel as `YYYY-MM-DD` strings. They are never turned into a local
 * `Date` and back, which is where an off-by-one day would creep in.
 */

/** The offset, as it appears in an ISO timestamp. */
export const BAGHDAD_OFFSET = '+03:00';

const OFFSET_MS = 3 * 60 * 60 * 1000;
const DAY_MS = 86_400_000;
const DAY_RE = /^(\d{4})-(\d{2})-(\d{2})$/;

/** True for a real `YYYY-MM-DD` calendar day (so `2026-02-30` is false). */
export function isDay(value: string): boolean {
  const match = DAY_RE.exec(value);
  if (!match) return false;
  const at = Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  return new Date(at).toISOString().slice(0, 10) === value;
}

/** Today's date in Baghdad, whatever zone the browser is in. */
export function baghdadToday(now: Date = new Date()): string {
  return new Date(now.getTime() + OFFSET_MS).toISOString().slice(0, 10);
}

/**
 * The day `count` days after `day` (negative for before).
 *
 * Done in UTC on purpose: a UTC calendar has no daylight-saving jumps, so
 * adding N days always lands N calendar days away.
 */
export function addDays(day: string, count: number): string {
  const match = DAY_RE.exec(day);
  if (!match) throw new Error(`not a YYYY-MM-DD day: ${day}`);
  const at = Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  return new Date(at + count * DAY_MS).toISOString().slice(0, 10);
}

/** Days from `from` to `to`, counting both ends (so a single day is 1). */
export function daysInclusive(from: string, to: string): number {
  return Math.round((Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / DAY_MS) + 1;
}

/**
 * The instant a Baghdad day starts, as an ISO timestamp carrying `+03:00`.
 *
 * For the endpoints that still take timestamps rather than days: sending the
 * offset explicitly means the server never has to guess, and a range
 * `[dayStart(from), dayStart(addDays(to, 1)))` is exactly the Baghdad days
 * `from`..`to` with no second shaved off the end.
 */
export function dayStart(day: string): string {
  return `${day}T00:00:00${BAGHDAD_OFFSET}`;
}

export type RangePreset = 'today' | '7d' | '30d' | '90d' | '12m';

export interface DayRange {
  /** First day, inclusive. */
  from: string;
  /** Last day, inclusive — today for every preset. */
  to: string;
}

/**
 * The inclusive day range a preset means, ending today (Baghdad).
 *
 * "Last 7 days" is today and the six before it — seven buckets, the last of
 * them still counting. "Last 12 months" starts on the 1st of the month eleven
 * months back rather than on today's date a year ago, so a monthly bucketing
 * of it is twelve whole months (the current one partial) instead of a
 * thirteenth sliver at the start that looks like a quiet month.
 */
export function presetRange(preset: RangePreset, today: string = baghdadToday()): DayRange {
  switch (preset) {
    case 'today':
      return { from: today, to: today };
    case '7d':
      return { from: addDays(today, -6), to: today };
    case '30d':
      return { from: addDays(today, -29), to: today };
    case '90d':
      return { from: addDays(today, -89), to: today };
    case '12m': {
      const [year, month] = today.split('-').map(Number) as [number, number];
      const start = new Date(Date.UTC(year, month - 1 - 11, 1));
      return { from: start.toISOString().slice(0, 10), to: today };
    }
  }
}

/**
 * The earliest exam date the studies report accepts (the server's
 * `EXAM_DATE_MIN`). A StudyDate before it is a typo or a default, not an
 * exam, and the catalogue treats it as no date at all.
 */
export const EXAM_DATE_MIN = '1990-01-01';

/** Calendar windows of the studies report, each ending today (Baghdad). */
export type CalendarPreset = 'month' | '3m' | 'year' | 'all';

/**
 * The inclusive day range a calendar preset means, ending today.
 *
 * Unlike `presetRange`, every window starts on a calendar boundary — the 1st
 * of a month, or January 1st — because the studies report compares it with
 * the same calendar stretch before it ("this month so far" against the same
 * days of last month), and that comparison needs a window starting on the 1st.
 */
export function calendarRange(preset: CalendarPreset, today: string = baghdadToday()): DayRange {
  const [year, month] = today.split('-').map(Number) as [number, number];
  switch (preset) {
    case 'month':
      return { from: `${today.slice(0, 7)}-01`, to: today };
    case '3m': {
      const start = new Date(Date.UTC(year, month - 1 - 2, 1));
      return { from: start.toISOString().slice(0, 10), to: today };
    }
    case 'year':
      return { from: `${year}-01-01`, to: today };
    case 'all':
      return { from: EXAM_DATE_MIN, to: today };
  }
}
